import templateMediaManifestData from './template-media.manifest.json'

export interface TemplateMediaManifestEntry {
  localPath: string
  bytes?: number
  sha256?: string
  contentType?: string
}

export interface TemplateMediaManifest {
  version: number
  templates: Record<string, Record<string, TemplateMediaManifestEntry>>
}

export const templateMediaManifest = templateMediaManifestData as TemplateMediaManifest

export interface TemplateMediaRef {
  templateId: string
  key: string
}

export interface SignedMediaUrl {
  url: string
  expiresAt: string
}

export interface SignedTemplateMediaResponse {
  items: Record<string, SignedMediaUrl>
  ttlSeconds?: number
}

export interface SignedMediaResolverOptions {
  apiBase: string
  fetchImpl?: typeof fetch
  now?: () => number
  refreshMarginMs?: number
  batchDelayMs?: number
  siblingKeys?: (templateId: string) => string[]
}

export interface SignedMediaResolver {
  resolve(templateId: string, key: string): Promise<string | null>
  invalidate(templateId: string, key: string): boolean
}

const DEFAULT_REFRESH_MARGIN_MS = 60_000
const DEFAULT_BATCH_DELAY_MS = 0
const RETRY_WINDOW_MS = 60_000
const MAX_BATCH_KEYS = 20
const MAX_THROTTLE_RETRIES = 2
const DEFAULT_RETRY_DELAYS_MS = [1_000, 2_000] as const

// Mirrors API_BASE in infrastructure/lib/api.ts — kept local so media consumers
// never drag the api module (and its vi.mock surface in template tests) in.
const SIGN_API_BASE = `${(import.meta.env as unknown as EnvBag | undefined)?.VITE_API_URL || 'http://localhost:4400'}/v1`

export const SIGNED_TEMPLATE_MEDIA_FLAG = 'VITE_SIGNED_TEMPLATE_MEDIA'

type EnvBag = Record<string, string | undefined>

const envBag = (): EnvBag => import.meta.env as unknown as EnvBag

export function isSignedTemplateMediaEnabled(): boolean {
  return envBag()[SIGNED_TEMPLATE_MEDIA_FLAG] === 'true'
}

export function resolveTemplateMedia(
  localPath: string | undefined,
  manifest: TemplateMediaManifest = templateMediaManifest,
): TemplateMediaRef | null {
  if (!localPath) return null
  for (const [templateId, entries] of Object.entries(manifest.templates)) {
    for (const [key, entry] of Object.entries(entries ?? {})) {
      if (entry?.localPath === localPath) return { templateId, key }
    }
  }
  return null
}

export function templateSignUrl(
  apiBase: string,
  templateId: string,
  keys: readonly string[],
): string {
  const base = apiBase.replace(/\/+$/, '')
  const keysParam = keys.map((key) => encodeURIComponent(key)).join(',')
  return `${base}/media/templates/${encodeURIComponent(templateId)}/sign?keys=${keysParam}`
}

interface CacheEntry {
  url: string
  expiresAtMs: number
}

interface Waiter {
  promise: Promise<string | null>
  deliver: (url: string | null) => void
}

type FakeableTimeout = ReturnType<typeof setTimeout>

export function createSignedMediaResolver(options: SignedMediaResolverOptions): SignedMediaResolver {
  const apiBase = options.apiBase
  const fetchImpl: typeof fetch =
    options.fetchImpl ??
    ((input: RequestInfo | URL, init?: RequestInit) => globalThis.fetch(input, init))
  const nowMs = options.now ?? ((): number => Date.now())
  const refreshMarginMs = options.refreshMarginMs ?? DEFAULT_REFRESH_MARGIN_MS
  const batchDelayMs = options.batchDelayMs ?? DEFAULT_BATCH_DELAY_MS
  const siblingKeys = options.siblingKeys

  const cache = new Map<string, CacheEntry>()
  const retryAt = new Map<string, number>()
  const waiters = new Map<string, Waiter>()
  const pendingKeys = new Map<string, Set<string>>()
  const timers = new Map<string, FakeableTimeout>()

  const splitKey = (templateId: string, key: string): string => `${templateId}\u0000${key}`

  const isFresh = (entry: CacheEntry | undefined): entry is CacheEntry =>
    !!entry && entry.expiresAtMs - refreshMarginMs > nowMs()

  function scheduleFlush(templateId: string): void {
    if (timers.has(templateId)) return
    const timer = setTimeout(() => {
      timers.delete(templateId)
      flush(templateId)
    }, batchDelayMs)
    timers.set(templateId, timer)
  }

  function flush(templateId: string): void {
    const keys = pendingKeys.get(templateId)
    if (!keys || keys.size === 0) return
    pendingKeys.delete(templateId)
    void sendBatch(templateId, [...keys])
  }

  async function sendBatch(templateId: string, keys: string[]): Promise<void> {
    let items: SignedTemplateMediaResponse['items'] | undefined
    let ttlSeconds: number | undefined
    for (let attempt = 0; ; attempt += 1) {
      try {
        const res = await fetchImpl(templateSignUrl(apiBase, templateId, keys))
        if (res.status === 429) {
          if (attempt >= MAX_THROTTLE_RETRIES) break
          await wait(retryDelayMs(res, attempt))
          continue
        }
        if (res.ok) {
          const payload = unwrapSignPayload(await res.json())
          items = payload?.items
          ttlSeconds = payload?.ttlSeconds
        }
      } catch {
        items = undefined
      }
      break
    }
    for (const key of keys) {
      const ck = splitKey(templateId, key)
      const item = items?.[key]
      const url = typeof item?.url === 'string' && item.url !== '' ? item.url : null
      if (url) {
        cache.set(ck, { url, expiresAtMs: expiresAtMsOf(item, ttlSeconds, nowMs()) })
      }
      const waiter = waiters.get(ck)
      waiters.delete(ck)
      waiter?.deliver(url)
    }
  }

  function enqueue(templateId: string, key: string): void {
    const ck = splitKey(templateId, key)
    if (!waiters.has(ck)) {
      let deliver!: (url: string | null) => void
      const promise = new Promise<string | null>((settle) => {
        deliver = settle
      })
      waiters.set(ck, { promise, deliver })
    }
    let keys = pendingKeys.get(templateId)
    if (!keys) {
      keys = new Set<string>()
      pendingKeys.set(templateId, keys)
    }
    keys.add(key)
  }

  const resolve = (templateId: string, key: string): Promise<string | null> => {
    if (!templateId || !key) return Promise.resolve(null)
    const ck = splitKey(templateId, key)
    const cached = cache.get(ck)
    if (isFresh(cached)) return Promise.resolve(cached.url)
    const inFlight = waiters.get(ck)
    if (inFlight) return inFlight.promise

    enqueue(templateId, key)

    const siblings = siblingKeys?.(templateId) ?? []
    for (const sibling of siblings) {
      if (!sibling || sibling === key) continue
      const keys = pendingKeys.get(templateId)
      if (keys && keys.size >= MAX_BATCH_KEYS) break
      const siblingCk = splitKey(templateId, sibling)
      if (isFresh(cache.get(siblingCk)) || waiters.has(siblingCk)) continue
      enqueue(templateId, sibling)
    }

    scheduleFlush(templateId)
    return waiters.get(ck)!.promise
  }

  const invalidate = (templateId: string, key: string): boolean => {
    if (!templateId || !key) return false
    const ck = splitKey(templateId, key)
    const now = nowMs()
    const last = retryAt.get(ck)
    if (last !== undefined && now - last < RETRY_WINDOW_MS) return false
    retryAt.set(ck, now)
    cache.delete(ck)
    return true
  }

  return { resolve, invalidate }
}

function wait(ms: number): Promise<void> {
  return new Promise((settle) => {
    setTimeout(settle, ms)
  })
}

function retryDelayMs(res: Response, attempt: number): number {
  const header = res.headers?.get?.('Retry-After')?.trim()
  if (header) {
    const seconds = Number(header)
    if (Number.isFinite(seconds) && seconds >= 0) return Math.min(seconds * 1000, 5_000)
  }
  return DEFAULT_RETRY_DELAYS_MS[Math.min(attempt, DEFAULT_RETRY_DELAYS_MS.length - 1)]
}

function expiresAtMsOf(
  item: SignedMediaUrl | undefined,
  ttlSeconds: number | undefined,
  fallbackNow: number,
): number {
  if (item && typeof item.expiresAt === 'string') {
    const fromIso = Date.parse(item.expiresAt)
    if (!Number.isNaN(fromIso)) return fromIso
  }
  if (typeof ttlSeconds === 'number' && Number.isFinite(ttlSeconds) && ttlSeconds > 0) {
    return fallbackNow + ttlSeconds * 1000
  }
  return fallbackNow
}

function unwrapSignPayload(body: unknown): SignedTemplateMediaResponse | null {
  if (!body || typeof body !== 'object') return null
  const withData = body as { data?: unknown }
  const payload = withData.data && typeof withData.data === 'object' ? withData.data : body
  const candidate = payload as { items?: unknown; ttlSeconds?: unknown }
  if (!candidate.items || typeof candidate.items !== 'object' || Array.isArray(candidate.items)) {
    return null
  }
  return {
    items: candidate.items as SignedTemplateMediaResponse['items'],
    ttlSeconds:
      typeof candidate.ttlSeconds === 'number' && Number.isFinite(candidate.ttlSeconds)
        ? candidate.ttlSeconds
        : undefined,
  }
}

export const signedMedia: SignedMediaResolver = createSignedMediaResolver({
  apiBase: SIGN_API_BASE,
  siblingKeys: (templateId: string): string[] =>
    Object.entries(templateMediaManifest.templates[templateId] ?? {})
      .filter(([, entry]) => !!entry?.sha256)
      .map(([key]) => key),
})
